import type { Site } from '@bk/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ReactNode } from 'react';
import { api, apiObjectUrl, apiUpload, errorMessage } from '../lib/api';
import { useBrandData, useTenantLogo } from '../lib/brand';
import { Button, ErrorText } from '../ui';

/** Il logo di una sede (ADR 0210 § 3), scaricato una volta; stessa cache del menu. */
export function SiteLogo({ tenantId, s, className = 'h-8 max-w-[8rem] object-contain' }: { tenantId: string; s: Site; className?: string }) {
  const logo = useQuery({
    queryKey: ['tenant', tenantId, 'site-logo', s.id],
    queryFn: () => apiObjectUrl(`/tenants/${tenantId}/brand/sites/${s.id}/logo`),
    enabled: !!s.marchio?.logo,
    staleTime: Infinity,
  });
  return s.marchio?.logo && logo.data ? <img src={logo.data} alt={`Logo di ${s.name}`} className={className} /> : null;
}

/**
 * Il marchio della sede (ADR 0210 § 3): quello trovato dal suo sito o quello dell'azienda; il titolare carica il logo,
 * sceglie i colori, indica il sito della sede o lo fa cercare, e può tornare al marchio dell'azienda.
 */
export function SiteBrandEditor({ tenantId, s, manager, companyLogo }: { tenantId: string; s: Site; manager: boolean; companyLogo: string | null }) {
  const qc = useQueryClient();
  return (
    <BrandEditor
      base={`/tenants/${tenantId}/brand/sites/${s.id}`}
      colori={s.marchio?.colori ?? []}
      sito={s.marchio?.sito ?? null}
      manager={manager}
      sitePlaceholder="www.sito-della-sede.it (vuoto: lo cerco io)"
      resetLabel={s.marchio ? 'Usa quello dell’azienda' : null}
      shown={
        s.marchio ? (
          <SiteLogo tenantId={tenantId} s={s} />
        ) : (
          <span className="flex items-center gap-2 text-sm text-ink/60">
            {companyLogo && <img src={companyLogo} alt="" className="h-6 max-w-[6rem] object-contain" />}
            Usa il marchio dell’azienda
          </span>
        )
      }
      onDone={() => {
        void qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'sites'] });
        void qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'site-logo', s.id] });
      }}
    />
  );
}

/**
 * Il marchio dell'azienda (ADR 0117, 0210 § 3): letto dal sito ufficiale, oppure deciso dal titolare quando il sito non
 * c'è o è quello di una sede.
 */
export function CompanyBrandEditor({ tenantId, manager }: { tenantId: string; manager: boolean }) {
  const qc = useQueryClient();
  const brand = useBrandData(tenantId);
  const logo = useTenantLogo(tenantId);
  const b = brand.data;
  if (!b) return null;
  const note = b.sito_di_sede
    ? `Il sito trovato è quello della sede ${b.sito_di_sede}: il suo marchio è della sede. Carica il logo dell’azienda o indica il sito della società.`
    : b.stato === 'nessun_sito' && !b.dal_titolare
      ? 'Non ho trovato un sito della società: carica il logo o indica il sito.'
      : null;
  return (
    <BrandEditor
      base={`/tenants/${tenantId}/brand`}
      colori={b.colori.length ? [b.colore ?? b.colori[0]!, ...b.colori.filter((c) => c !== b.colore)] : []}
      sito={b.sito}
      manager={manager}
      sitePlaceholder="www.sito-della-societa.it"
      resetLabel={b.dal_titolare ? 'Rileggi dal sito ufficiale' : null}
      shown={
        <span className="flex flex-wrap items-center gap-3 text-sm text-ink/60">
          {logo ? <img src={logo} alt="Logo dell’azienda" className="h-8 max-w-[8rem] object-contain" /> : !note && 'Nessun logo'}
          {note && <span>{note}</span>}
        </span>
      }
      onDone={() => void qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'brand'] })}
    />
  );
}

/** Logo, colori e sito di un marchio, con le azioni del titolare. */
function BrandEditor({
  base,
  colori,
  sito,
  manager,
  sitePlaceholder,
  resetLabel,
  shown: current,
  onDone,
}: {
  base: string;
  colori: string[];
  sito: string | null;
  manager: boolean;
  sitePlaceholder: string;
  resetLabel: string | null;
  shown: ReactNode;
  onDone: () => void;
}) {
  const file = useRef<HTMLInputElement>(null);
  const [site, setSite] = useState<string | null>(null);
  const [colors, setColors] = useState<string[] | null>(null);
  const run = useMutation({
    mutationFn: (f: () => Promise<unknown>) => f(),
    onSuccess: () => {
      onDone();
      setSite(null);
      setColors(null);
    },
  });
  const busy = run.isPending;
  const shown = colors ?? colori;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {current}
        {sito && <span className="text-xs text-ink/55">dal sito {sito.replace(/^https?:\/\//, '')}</span>}
      </div>
      {(shown.length > 0 || manager) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {shown.map((c, i) => (
            <label key={i} className="relative h-6 w-6 cursor-pointer overflow-hidden rounded-full border border-black/10" style={{ background: c }} title={c}>
              {manager && <input type="color" className="absolute inset-0 cursor-pointer opacity-0" value={c} onChange={(e) => setColors(shown.map((x, j) => (j === i ? e.target.value : x)))} aria-label={`Colore ${i + 1}`} />}
            </label>
          ))}
          {manager && shown.length < 6 && (
            <button type="button" className="grid h-6 w-6 place-items-center rounded-full border border-dashed border-black/20 text-xs text-ink/50 hover:text-ink" onClick={() => setColors([...shown, '#336699'])} aria-label="Aggiungi un colore">
              +
            </button>
          )}
          {manager && colors && (
            <>
              <Button variant="ghost" className="!px-2 !py-1 text-xs" disabled={busy} onClick={() => run.mutate(() => api(base, { method: 'PATCH', body: { colori: colors } }))}>
                Salva i colori
              </Button>
              <Button variant="ghost" className="!px-2 !py-1 text-xs" disabled={busy} onClick={() => setColors(null)}>
                Annulla
              </Button>
            </>
          )}
        </div>
      )}
      {manager && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <input
            ref={file}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) run.mutate(() => apiUpload(`${base}/logo`, f));
            }}
          />
          <Button variant="ghost" className="!px-2 !py-1 text-xs" disabled={busy} onClick={() => file.current?.click()}>
            Carica il logo
          </Button>
          {site === null ? (
            <Button variant="ghost" className="!px-2 !py-1 text-xs" disabled={busy} onClick={() => setSite(sito ?? '')}>
              Prendilo da un sito
            </Button>
          ) : (
            <span className="flex flex-wrap items-center gap-1">
              <input className="w-56 rounded-md border border-black/15 px-2 py-1 text-xs" placeholder={sitePlaceholder} value={site} onChange={(e) => setSite(e.target.value)} aria-label="Sito" />
              <Button variant="ghost" className="!px-2 !py-1 text-xs" disabled={busy} onClick={() => run.mutate(() => api(base, { method: 'PATCH', body: { sito: site.trim() || null } }))}>
                {busy ? 'Leggo…' : 'Leggi'}
              </Button>
              <Button variant="ghost" className="!px-2 !py-1 text-xs" disabled={busy} onClick={() => setSite(null)}>
                Annulla
              </Button>
            </span>
          )}
          {resetLabel && (
            <Button variant="ghost" className="!px-2 !py-1 text-xs" disabled={busy} onClick={() => run.mutate(() => api(base, { method: 'DELETE' }))}>
              {resetLabel}
            </Button>
          )}
        </div>
      )}
      <ErrorText>{run.error ? errorMessage(run.error) : ''}</ErrorText>
    </div>
  );
}
